import React from "react";
import classNames from "classnames";
import Size from "../../../enums/Size";
import { InputType } from "../../enum";
import { MoneyInputConfig } from "../types";
import Variant from "../../../enums/Variant";
import Input from "react-currency-input-field";
import { getCurrencySymbol, getMinimumFractionDigits } from "shared/utils";

function MoneyInput({ type = InputType.TEXT, onChange, ...props }: MoneyInputConfig) {
  const computedClassNames = React.useMemo(
    () => getClassNames(props.className, props.variant, props.size, props.hasError),
    [props.className, props.variant, props.size, props.hasError]
  );

  const { prefix, suffix } = React.useMemo(() => {
    let prefix = props.prefix;
    let suffix = props.suffix;
    if (props.currencyCode) {
      const currencySymbol = getCurrencySymbol(props.currencyCode, props.locale);
      if (!prefix && currencySymbol.isPrefix) {
        prefix = currencySymbol.symbol;
      }
    }
    return { prefix, suffix };
  }, [props.currencyCode, props.locale, props.prefix, props.suffix]);

  const intlConfig = React.useMemo(() => {
    if (props.locale) {
      return {
        locale: props.locale,
        currency: props.currencyCode
      };
    }
  }, [props.currencyCode, props.locale]);

  //if value is decimal then it should always have minimum 2 digit. Example: 10.50 instead of 10.5
  const decimalScale = React.useMemo(() => getMinimumFractionDigits(parseFloat(props.formValue)), [props.formValue]);

  const changeHandler = React.useCallback(
    value => {
      onChange?.(value || "");
    },
    [onChange]
  );

  return (
    <div
      className={classNames(computedClassNames, props.className, {
        "iconPosition--right": props.switchIconPosition
      })}>
      {!props.switchIconPosition && props.icon ? (
        <span className="input_icon" data-testid={`${props.name}_iconPosition--left`}>
          {props.icon}
        </span>
      ) : null}
      <Input
        id="input-example"
        name={props.name}
        value={props.value}
        placeholder={props.placeholder}
        disabled={props.disabled}
        decimalsLimit={2}
        decimalScale={decimalScale}
        prefix={prefix}
        suffix={suffix}
        disableGroupSeparators={props.disableGroupSeparators}
        disableAbbreviations={true}
        intlConfig={intlConfig}
        onValueChange={changeHandler}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
      />
      {props.switchIconPosition && props.icon ? (
        <span className="input_icon" data-testid={`${props.name}_iconPosition--right`}>
          {props.icon}
        </span>
      ) : null}
    </div>
  );
}

export default MoneyInput;

function getClassNames(className?: string, variant?: Variant, size?: Size, hasError?: boolean | string) {
  return classNames(className, {
    inputBox: true,
    [`inputBox--${variant}`]: variant,
    [`inputBox--${size}`]: size,
    [`inputBox--hasError`]: hasError
  });
}
